<template>
  <div>
    <v-app>
      <!-- Debug info (remove in production) -->
      <div
        v-if="showDebugInfo"
        style="position: fixed; top: 0; left: 0; background: black; color: white; padding: 10px; z-index: 9999; font-size: 12px;"
      >
        Debug: currentProgram={{ !!currentProgram }}, loading={{ loading }},
        authReady={{ authReady }}, currentUser={{ !!currentUser }},
        shouldRedirect={{ shouldRedirectToSignin }}, domainError={{
          domainError
        }}, programId={{ programId }}, firebaseUser={{
          auth && auth.currentUser ? true : false
        }}, firebaseEmail={{
          auth && auth.currentUser ? auth.currentUser.email : "none"
        }}
      </div>

      <core-view
        v-if="currentProgram && !loading && authReady && currentUser"
      />
      <!-- Show auth pages when on signin/signup routes - allow even during loading -->
      <router-view
        v-else-if="
          authReady &&
            !currentUser &&
            !domainError &&
            currentProgram &&
            isOnAuthPage
        "
      />
      <NoProgram
        v-else-if="
          authReady && !currentUser && !shouldRedirectToSignin && domainError
        "
      />
      <!-- Show loading spinner only when NOT on auth pages -->
      <div
        v-else-if="(!authReady || loading) && !isOnAuthPage"
        class="d-flex justify-center align-center"
        style="height: 100vh;"
      >
        <v-progress-circular
          color="grey lighten-2"
          indeterminate
          size="130"
          width="10"
        ></v-progress-circular>
      </div>
      <!-- Fallback for when auth is ready but no user and no domain error -->
      <div
        v-else-if="
          authReady &&
            !currentUser &&
            !domainError &&
            currentProgram &&
            !isOnAuthPage
        "
        class="d-flex justify-center align-center"
        style="height: 100vh;"
      >
        <v-progress-circular
          color="grey lighten-2"
          indeterminate
          size="130"
          width="10"
        ></v-progress-circular>
      </div>
      <snack-bar />
    </v-app>
  </div>
</template>

<script>
import { auth, authReadyPromise } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";

export default {
  components: {
    CoreView: () => import("@/components/core/View"),
    SnackBar: () => import("@/components/core/Snackbar"),
    NoProgram: () =>
      import(
        "@/components/Design/View/ViewComponents/Shared/Authentication/NoProgram.vue"
      )
  },
  data() {
    return {
      userDataLoaded: false, // Flag to track if user data has been loaded
      authReady: false, // Flag to track if auth state is ready
      authUnsubscribe: null, // Store the auth listener unsubscribe function
      authInitialized: false, // Flag to track if auth has been initialized
      shouldRedirectToSignin: false, // Flag to track if we should redirect to signin
      showDebugInfo: false, // Disable debug info for production
      gettingCurrentUser: false // Flag to prevent multiple getCurrentUser calls
    };
  },
  computed: {
    loading() {
      return this.$store.getters.loading;
    },
    admin() {
      return this.$store.getters.admin;
    },
    programId() {
      return this.$store.getters.programId;
    },
    selectedPageTemplate() {
      return this.$store.getters.selectedPageTemplate;
    },
    currentUser() {
      return this.$store.getters.currentUser;
    },
    currentProgram() {
      return this.$store.getters.currentProgram;
    },
    firstTimeLoad() {
      return this.$store.getters.firstTimeLoad;
    },
    domainError() {
      return this.$store.getters.domainError;
    },
    auth() {
      return auth;
    },
    isOnAuthPage() {
      const currentRoute = this.$route.path;
      return (
        currentRoute.includes("/signin") ||
        currentRoute.includes("/signup") ||
        currentRoute.includes("/demo")
      );
    }
  },
  watch: {
    currentUser(newValue, oldValue) {
      // Only trigger loadUserData if we have a user and data hasn't been loaded yet
      // AND if we're not dealing with an anonymous user without proper authentication
      if (
        newValue &&
        !this.userDataLoaded &&
        this.authReady &&
        this.authInitialized &&
        newValue.email // Only load data if the user has an email
      ) {
        this.loadUserData();
      } else if (!newValue && this.userDataLoaded) {
        // Reset the flag when the user logs out
        this.userDataLoaded = false;
      }
    },
    authReady(newValue) {
      // When auth becomes ready, check what to do
      if (newValue) {
        this.checkAppState();
      }
    },
    programId(newValue) {
      // When program ID is set, check what to do
      if (newValue) {
        this.checkAppState();
      }
    },
    currentProgram(newValue) {
      if (newValue) {
        this.checkAppState();
      }
    },
    domainError(newValue) {
      // Domain error changed
    }
  },
  methods: {
    setLanguage() {},

    // Method to refresh translations when needed
    async refreshTranslations() {
      try {
        console.log("🔄 Refreshing translations...");
        await this.$store.dispatch("loadTranslationData");
        console.log("✅ Translations refreshed successfully");
      } catch (error) {
        console.error("❌ Error refreshing translations:", error);
      }
    },

    checkAppState() {
      // If auth is ready and we have a program but no user and no domain error, redirect to signin
      // BUT only if we're not already on an auth page (prevent loops)
      if (
        this.authReady &&
        !this.currentUser &&
        this.currentProgram &&
        !this.domainError &&
        !this.shouldRedirectToSignin &&
        !this.isOnAuthPage // Don't redirect if already on auth page
      ) {
        this.redirectToSignin();
      } else if (
        this.authReady &&
        this.currentUser &&
        !this.userDataLoaded &&
        this.authInitialized
      ) {
        this.loadUserData();
      }
    },

    redirectToSignin() {
      // Only redirect if we're not already on an auth page and not in a domain error state
      const currentRoute = this.$route.path;

      if (
        !currentRoute.includes("/signin") &&
        !currentRoute.includes("/signup") &&
        !currentRoute.includes("/demo")
      ) {
        this.shouldRedirectToSignin = true;
        this.loadingMessage = "Redirecting to sign in...";

        // Use nextTick to ensure the redirect happens after the current update cycle
        this.$nextTick(() => {
          this.$router.push("/signin").catch(err => {
            console.error("Router push error:", err);
            // If router push fails, try replace
            this.$router.replace("/signin").catch(err2 => {
              console.error("Router replace error:", err2);
            });
          });
        });
      } else {
        // Reset the flag since we're already on an auth page
        this.shouldRedirectToSignin = false;
        this.loadingMessage = "Ready for authentication...";
      }
    },

    async loadUserData() {
      try {
        // Check if user exists and auth is ready before proceeding
        if (!this.currentUser || !this.authReady || !this.authInitialized) {
          return;
        }

        // Set flag to prevent multiple calls
        this.userDataLoaded = true;

        // Load data sequentially to avoid overwhelming Firestore
        try {
          await this.safeDispatchAsync("getPromotionList", this.currentUser);
          await this.safeDispatchAsync("getProgressData", this.currentUser);
          await this.safeDispatchAsync("getPrimaryMenus", this.currentUser);
          await this.safeDispatchAsync("getUserDealer", this.currentUser);
          await this.safeDispatchAsync("getSecondaryMenus", this.currentUser);
          await this.safeDispatchAsync("getRewardList", this.currentUser);
          await this.safeDispatchAsync("getOfferList", this.currentUser);
          // await this.safeDispatchAsync("loadQuizzes");
        } catch (error) {
          console.error("Error loading user data:", error);
          // Reset flag if loading fails so we can try again
          this.userDataLoaded = false;
        }
      } catch (error) {
        // Reset flag if loading fails so we can try again
        this.userDataLoaded = false;
        console.error("Error in loadUserData:", error);
      }
    },
    async safeDispatchAsync(action, payload) {
      try {
        await this.$store.dispatch(action, payload);
      } catch (error) {
        console.error(`Error dispatching ${action}:`, error);
        // Don't throw to allow other actions to continue
      }
    },
    safeDispatch(action, payload) {
      try {
        this.$store.dispatch(action, payload);
      } catch (error) {
        console.error(`Error dispatching ${action}:`, error);
      }
    },
    setupAuthListener() {
      // Set up Firebase auth state listener
      this.authUnsubscribe = onAuthStateChanged(auth, async user => {
        this.authReady = true;

        if (user) {
          if (!(await this.$store.dispatch("requireMemberSession", user))) {
            this.authInitialized = true;
            return;
          }

          // User is signed in, now we can safely access Firestore
          this.shouldRedirectToSignin = false;

          // Only try to get current user if this is not an anonymous user without email
          // (Anonymous users are used for demo mode and don't have member records)
          if (!user.isAnonymous || user.email) {
            // If we don't have a current user in store yet, try to get it
            if (
              !this.currentUser &&
              !this.gettingCurrentUser &&
              !this.$store.getters.isAuthenticating
            ) {
              try {
                this.gettingCurrentUser = true;
                const restoredUser = await this.$store.dispatch(
                  "getCurrentUser"
                );
              } catch (error) {
                console.error(
                  "❌ Error getting current user from auth listener:",
                  error
                );
              } finally {
                this.gettingCurrentUser = false;
              }
            }
          }
        } else {
          // User is signed out
          this.shouldRedirectToSignin = true;
        }

        // Mark auth as initialized after first state change
        this.authInitialized = true;
      });
    },

    async initializeAuth() {
      try {
        // Wait for Firebase auth to be ready
        await authReadyPromise;

        this.authInitialized = true;
        this.authReady = true; // Set authReady to true once Firebase auth is initialized
        return auth.currentUser;
      } catch (error) {
        console.error("Error initializing auth:", error);
        this.authInitialized = true; // Set to true even on error to prevent hanging
        this.authReady = true; // Set to true even on error to prevent hanging
        return null;
      }
    }
  },
  async beforeCreate() {
    try {
      await this.$store.dispatch("setLoading", true);

      // Clean up any existing auth listeners first
      await this.$store.dispatch("cleanupAuthListeners");

      // Initialize auth first before any Firestore operations
      await this.initializeAuth();

      // Set program ID - this needs to run for multi-tenant domain resolution
      console.log("🏗️ App: Setting program ID based on domain/route");
      // Now safe to do Firestore operations
      if (!this.$route.path.includes("/demo")) {
        // Normal route - use domain resolution
        await this.$store.dispatch("setProgramId");
      } else {
        // Demo route - extract program ID from URL params
        const programId = this.$route.params.id;
        if (programId) {
          await this.$store.dispatch("setProgramId", programId);
        }
      }

      // Load translations early - before user authentication
      // This ensures signin/signup forms are translated
      try {
        console.log(
          "🌐 Loading translations for program:",
          this.$store.getters.programId
        );
        await this.$store.dispatch("loadTranslations");
        await this.$store.dispatch("loadTranslationData");

        // Set initial locale from program settings
        const initialLocale = this.$store.getters.initialLocale || "en";
        console.log("🌐 Setting initial locale to:", initialLocale);
        this.$i18n.setLocaleWithFallback(initialLocale);
      } catch (error) {
        console.error("❌ Error loading translations:", error);
        // Continue with fallback translations
      }

      // Check firstTimeLoad - simplified logic
      const firstTimeLoad = this.$store.getters.firstTimeLoad;
      if (firstTimeLoad) {
        await this.$store.dispatch("setFirstTimeLoad", false);
      }
    } catch (error) {
      console.error("Error in beforeCreate:", error);
    } finally {
      // Always set loading to false, even if there are errors
      await this.$store.dispatch("setLoading", false);
    }
  },
  async created() {
    try {
      // Set up auth state listener
      this.setupAuthListener();

      // Set favicon
      const favicon = document.querySelector('[rel="icon"]');
      if (favicon) {
        favicon.setAttribute("href", "incentable.png");
      }

      // If we have a current user and auth is initialized, try to get current user data
      // Only if this is not an anonymous user without email
      const firebaseUser = this.auth?.currentUser;
      if (
        this.authInitialized &&
        !this.currentUser &&
        !this.gettingCurrentUser &&
        firebaseUser &&
        (!firebaseUser.isAnonymous || firebaseUser.email)
      ) {
        try {
          this.gettingCurrentUser = true;
          await this.$store.dispatch("getCurrentUser");
        } catch (error) {
          console.error("Error getting current user in created:", error);
        } finally {
          this.gettingCurrentUser = false;
        }
      }
    } catch (error) {
      console.error("Error in App creation:", error);
    }
  },
  mounted() {
    // Final check after everything is mounted
    this.$nextTick(() => {
      this.checkAppState();
    });
  },
  beforeDestroy() {
    // Clean up auth listener
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
    }
  }
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Work+Sans:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Caveat:wght@400;500;600;700&family=Cinzel:wght@400;500;600;700;800;900&family=Lobster&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;500;600;700&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;0,900;1,300;1,400;1,700;1,900&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Pacifico&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500;1,600;1,700;1,800;1,900&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Satisfy&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Spectral:ital,wght@0,200;0,300;0,400;0,500;0,600;0,700;0,800;1,200;1,300;1,400;1,500;1,600;1,700;1,800&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Quicksand:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&family=Urbanist:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap");

.lottie {
  position: absolute;
  z-index: 1000;
}
.lottie-full-width {
  position: absolute;
  z-index: 1000;
  width: 100%;
}
.lottie-full-height {
  position: absolute;
  z-index: 1000;
  height: 100%;
}
.incentable {
  color: #e91e63;
}
.page-bar {
  margin-top: 128px !important;
}
.size-label {
  color: rgba(0, 0, 0, 0.6);
  letter-spacing: normal;
  font-family: "Roboto", sans-serif !important;
  font-size: 12px !important;
  font-weight: 300 !important;
  margin-top: 3px;
}
.page {
  padding: 0px 0px 0px 0px !important;
}
.program-page {
  padding: 20px 00px 0px 0px !important;
}
.el {
  box-shadow: rgba(0, 0, 0, 0.04) 0px 5px 6px -3px,
    rgba(0, 0, 0, 0.06) 0px 9px 12px 1px, rgba(0, 0, 0, 0.05) 0px 3px 16px 2px !important;
}
.v-application {
  font-weight: 400 !important;
  font-size: 15px !important;
  font-family: "Roboto", sans-serif !important;
  color: #4e4e4e !important;
}
.metric {
  font-size: 36px !important;
  font-weight: 400 !important;
}
.switch {
  margin-top: 22px !important;
}
.switch-label {
  margin-top: 2px !important;
  font-family: "Roboto", sans-serif !important;
  font-size: 11px !important;
  font-weight: 500 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.0892857143em;
  text-indent: 0.0892857143em;
  white-space: nowrap;
}
.text-icon {
  font-family: "Roboto", sans-serif !important;
  font-size: 12px !important;
  font-weight: 900 !important;
  color: #616161 !important;
}
.vue-dropzone {
  font-weight: 400 !important;
  font-size: 12px !important;
  font-family: "Lato", sans-serif !important;
  color: #ffffff !important;
}
.icon {
  color: #616161 !important;
}
.theme--dark.v-tabs > .v-tabs-bar {
  background-color: #2e2e2e !important;
}
.theme-menu {
  font-family: "Roboto", sans-serif !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  color: #757575 !important;
  line-height: 1.2;
}
.award {
  font-weight: 600 !important;
  font-size: 15px !important;
  font-family: "Roboto", sans-serif !important;
  color: #4e4e4e !important;
}
.link {
  text-decoration: none !important;
}
.back-link {
  font-weight: 400 !important;
  font-size: 11px !important;
  font-family: "Roboto", sans-serif !important;
}
.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}
.filename {
  font-family: "Roboto", sans-serif !important;
  font-size: 12px !important;
  font-weight: 400 !important;
  line-height: 1em;
}
.program-name {
  font-family: "Roboto", sans-serif !important;
  font-size: 20px;
  font-weight: 300 !important;
  color: white !important;
}
.domain-name {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  color: #acacac !important;
}
.filename {
  font-size: 14px;
  font-weight: 700;
}
.alert-text {
  font-family: "Roboto", sans-serif !important;
  font-size: 13px !important;
  font-weight: 400 !important;
}
.card-title {
  font-size: 15pt;
  font-weight: 300;
  letter-spacing: 1px;
}
.toolbar-title {
  font-family: "Roboto", sans-serif !important;
  font-size: 16px !important;
  font-weight: 300 !important;
  color: white !important;
}
.page-heading {
  font-family: "Roboto", sans-serif !important;
  font-size: 20px !important;
  font-weight: 300 !important;
  color: #4e4e4e !important;
}
.v-breadcrumbs__item {
  font-family: "Roboto", sans-serif !important;
  font-size: 16px !important;
  font-weight: 300 !important;
  color: white !important;
}
.toolbar-card-heading {
  font-family: "Roboto", sans-serif !important;
  font-size: 18px !important;
  font-weight: 300 !important;
  color: white !important;
}
.toolbar-card-subheading {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px;
  font-weight: 200 !important;
  color: white !important;
}
.v-label {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px !important;
  font-weight: 400 !important;
}
.v-btn {
  font-family: "Roboto", sans-serif !important;
  font-size: 11px !important;
  font-weight: 500 !important;
  margin: 4px;
}
.v-card__text,
.v-card__title {
  word-break: normal !important;
}
.v-list-item__title {
  font-size: 14px !important;
  font-weight: 400 !important;
}
.tab {
  font-family: "Roboto", sans-serif !important;
  font-size: 11px !important;
  font-weight: 500 !important;
}
.incentable-heading {
  font-family: "Roboto", sans-serif !important;
  font-size: 30px;
  font-weight: 300 !important;
}
.page-heading {
  font-family: "Roboto", sans-serif !important;
  font-size: 18px !important;
  font-weight: 300 !important;
  color: #4e4e4e !important;
}
.incentable-page-subheading {
  font-family: "Roboto", sans-serif !important;
  font-size: 12px !important;
  font-weight: 400 !important;
  color: #4e4e4e !important;
}
.map-header {
  font-weight: 400 !important;
  background-color: #e0e0e0 !important;
}
.map-col-1 {
  background-color: #e0e0e0 !important;
  padding: 4px;
}
.map-cell {
  padding: 4px;
  margin: 0px 1px 1px 0px;
  min-height: 30px;
}
.import-form-header {
  font-family: "Roboto", sans-serif !important;
  font-size: 15px;
  font-weight: 600 !important;
  padding: 10px;
  margin: 0px 1px 1px 0px;
  min-height: 40px;
  background-color: #e0e0e0 !important;
}
.import-form {
  font-family: "Roboto", sans-serif !important;
  font-size: 15px;
  font-weight: 400 !important;
  padding: 10px;
  margin: 0px 1px 1px 0px;
  min-height: 42px;
}
.import-select {
  font-family: "Roboto", sans-serif !important;
  font-size: 15px !important;
  font-weight: 400 !important;
  margin: -4px 0px 0px 0px !important;
  padding: -10px 0px 0px 0px !important;
  width: 210px;
  height: 38px !important;
}
.dash-card-title {
  font-family: "Roboto", sans-serif !important;
  font-size: 15px;
  font-weight: 400 !important;
  margin-left: 0px !important;
  margin-bottom: 4px !important;
  color: #4e4e4e !important;
}
.file-title {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px;
  font-weight: 500 !important;
  margin-left: 2px !important;
  margin-bottom: 4px !important;
  color: #4e4e4e !important;
}
.dash-card {
  font-family: "Roboto", sans-serif !important;
  font-size: 11px;
  font-weight: 300 !important;
  line-height: 1.2;
  color: #acacac !important;
}
.incentable-form-bold {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px;
  font-weight: 500 !important;
  color: #4e4e4e !important;
}
.incentable-formula-heading {
  font-family: "Roboto", sans-serif !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  color: #4e4e4e !important;
}
.incentable-form-heading {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #4e4e4e !important;
}
.incentable-form-subheading {
  font-family: "Roboto", sans-serif !important;
  font-size: 12px !important;
  font-weight: 300 !important;
  color: #4e4e4e !important;
  margin-top: -8px;
  margin-bottom: 6px;
  line-height: 2.5;
}
.incentable-alert {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px !important;
  font-weight: 300 !important;
}
.close-icon {
  position: absolute;
  margin: -7px -17px;
}
.incentable-leaderboard-table-title-row {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  margin-right: 1px !important;
  margin-bottom: 1px !important;
  padding: 12px 22px !important;
  color: #4e4e4e !important;
  background-color: #e0e0e0 !important;
}
.table-title-row {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  margin-right: 1px !important;
  margin-bottom: 1px !important;
  padding: 18px 22px !important;
  color: #4e4e4e !important;
  background-color: #e0e0e0 !important;
}
.table-cell {
  font-weight: 400 !important;
  font-size: 14px !important;
  font-family: "Roboto", sans-serif !important;
  color: #4e4e4e !important;
  margin-right: 1px !important;
  margin-bottom: 1px !important;
  background-color: #f1f1f1 !important;
  padding: 10px 20px !important;
  line-height: 34px;
}
.incentable-csv-table-title-row {
  font-family: "Roboto", sans-serif !important;
  font-size: 10px !important;
  font-weight: 400 !important;
  margin-right: 1px !important;
  margin-bottom: 1px !important;
  padding: 10px !important;
  color: #4e4e4e !important;
  background-color: #e0e0e0 !important;
}
.incentable-csv-table-cell {
  font-weight: 400 !important;
  font-size: 14px !important;
  font-family: "Roboto", sans-serif !important;
}
.table-total-row {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  padding: 10px !important;
  color: #4e4e4e !important;
  background-color: #e0e0e0 !important;
}
.movable {
  cursor: move;
}
.table-first-col {
  margin-right: 1px !important;
  padding: 5px 20px !important;
  color: #4e4e4e !important;
  background-color: #e0e0e0 !important;
}
.table-icon {
  margin-right: 1px !important;
  margin-bottom: 2px !important;
  background-color: #1d1d1d !important;
  padding-top: 3px;
  padding-left: 3px;
  padding-bottom: 2px;
  line-height: 42px;
}
.table-icon-cell {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  margin-right: 1px !important;
  margin-bottom: 1px !important;
  background-color: #ffffff !important;
  padding-top: 1px;
  line-height: 34px;
  min-width: 45px;
}
.incentable-points-table-title-row {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #4e4e4e !important;
}
.cell-trim {
  padding-left: -6px;
  padding-right: -6px;
}
.operator {
  font-family: "Roboto", sans-serif !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  background-color: #e0e0e0;
  color: #2e2e2e !important;
}
.andor {
  font-family: "Roboto", sans-serif !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  background-color: #e0e0e0;
  color: #2e2e2e !important;
}
.rotate {
  transform: rotate(90deg);
}
.rotate180 {
  transform: rotate(180deg);
}
.rotate270 {
  transform: rotate(270deg);
}
.table-title {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #4e4e4e !important;
  background-color: #e0e0e0 !important;
}
.table-subtitle {
  font-family: "Roboto", sans-serif !important;
  font-size: 12px !important;
  font-weight: 400 !important;
  color: #4e4e4e !important;
}
.incentable-databucket-table-title {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  color: #888888 !important;
}
.incentable-databucket-table-subtitle {
  font-family: "Roboto", sans-serif !important;
  font-size: 10px !important;
  font-weight: 400 !important;
  color: #888888 !important;
}
.table-right {
  text-align: right;
  padding-right: 8px;
}
.table-left {
  text-align: left;
  padding-left: 8px;
}
.incentable-card-title {
  font-family: "Roboto", sans-serif !important;
  font-size: 18px !important;
  font-weight: 300 !important;
  margin-left: 8px;
  margin-right: 10px;
  margin-top: 0px;
  cursor: pointer;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  color: #4e4e4e !important;
}
.incentable-create-card-subtitle {
  font-family: "Roboto", sans-serif !important;
  font-size: 13px !important;
  font-weight: 300 !important;
  margin-left: 8px !important;
  margin-top: 0px !important;
  margin-bottom: 0px !important;
  color: #4e4e4e !important;
}
.incentable-card-subtitle {
  font-family: "Roboto", sans-serif !important;
  font-size: 13px !important;
  font-weight: 300 !important;
  margin-left: 8px !important;
  margin-top: -20px !important;
  margin-bottom: 0px !important;
  color: #4e4e4e !important;
}
.incentable-card-action-text {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px !important;
  font-weight: 300 !important;
  margin-left: 8px !important;
  margin-top: 4px !important;
  margin-bottom: 4px !important;
  color: #4e4e4e !important;
}
.incentable-dash-card-subtitle {
  font-family: "Roboto", sans-serif !important;
  font-size: 14px !important;
  font-weight: 300 !important;
  color: #4e4e4e !important;
}
.tab-table-padding {
  padding-left: 0px !important;
  padding-right: 0px !important;
  padding-top: 24px !important;
}
.menu-heading-label {
  font-family: "Roboto", sans-serif !important;
  font-size: 11px !important;
  font-weight: 400 !important;
  color: #acacac !important;
  margin-left: 14px;
}
.menu-label {
  font-weight: 300 !important;
  font-size: 13px !important;
  font-family: "Roboto", sans-serif !important;
  color: #e6e6e6 !important;
}
.v-list-item .v-list-item__subtitle,
.v-list-item .v-list-item__title {
  line-height: 2 !important;
}
.v-input {
  font-weight: 400 !important;
  font-size: 14px !important;
  font-family: "Roboto", sans-serif !important;
  color: #4e4e4e !important;
}
.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  font-weight: 500 !important;
  font-size: 14px !important;
  font-family: "Roboto", sans-serif !important;
  color: #4e4e4e !important;
}
.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  font-weight: 400 !important;
  font-size: 14px !important;
  font-family: "Roboto", sans-serif !important;
  color: #4e4e4e !important;
}
.v-data-table > .v-data-table__wrapper > table > tfoot > tr > th {
  font-weight: 500 !important;
  font-size: 14px !important;
  font-family: "Roboto", sans-serif !important;
  color: #4e4e4e !important;
}
.grid-block {
  border: 1px dashed grey !important;
  background-color: transparent !important;
}
.grid-outer {
  border: 1px dotted grey !important;
  background-color: transparent !important;
}
.grid-inner {
  border: 1px dotted white !important;
  background-color: transparent !important;
}
.grid-off {
  border: 1px dotted transparent !important;
  background-color: transparent !important;
}
.uppercase {
  text-transform: uppercase;
}
.capitalize {
  text-transform: capitalize;
}
.link {
  cursor: pointer;
}
.rounded-dialog {
  border-radius: 24px !important;
  overflow: hidden;
}
</style>
